import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';

import Button from '@/components/ui/button';
import PaymentScreen from '@/components/PaymentScreen';
import { GiftSummaryChip } from '@/components/LandingContext';

import { useOpenBankingRestartMutation } from '@/tsq/mutations';
import { redirectToExternal } from '@/utils/helpers/navigation.helpers';
import { formatCurrency } from '@/utils/formatters/currency';
import { miaConsoleLog } from '@/utils/mia-logger';
import {
  useAmount,
  useChildFirstName,
  useContributionId,
  useGiftImageUrl,
  useGiftInviteCode,
  useHydrated,
  useClearPaymentAttempt,
  useOpenBankingId,
  useShowChildPhoto,
} from '@/store';
import { openBankingStatuses } from '@/constants';

/** The outcomes this screen speaks for. `pending` is the polling timeout. */
type Outcome = 'failed' | 'cancelled' | 'pending';

const OUTCOMES: readonly Outcome[] = ['failed', 'cancelled', 'pending'];

function readOutcome(value: unknown): Outcome {
  return OUTCOMES.includes(value as Outcome) ? (value as Outcome) : 'failed';
}

/**
 * Copy per outcome. Headlines are the Jira ACs'; the shape — status strip,
 * reassurance, "you can try again" — is the design's. The banner names the
 * gift and the body names the pot, so the two do not repeat each other.
 */
const COPY: Record<
  Outcome,
  {
    banner: (gift: string) => string;
    heading: string;
    body: (pot: string) => string;
  }
> = {
  failed: {
    banner: (gift) => `Payment unsuccessful — ${gift} hasn't gone through.`,
    heading: "Your payment didn't go through",
    body: (pot) =>
      `Don't worry — nothing has been taken from your account, and nothing has reached ${pot}. You can try again below.`,
  },
  cancelled: {
    banner: (gift) => `Payment cancelled — ${gift} hasn't been sent.`,
    heading: 'You cancelled the payment',
    body: (pot) =>
      `Nothing has been taken from your account, and nothing has reached ${pot}. You can try again below.`,
  },
  pending: {
    banner: (gift) => `Payment pending — ${gift} is still with your bank.`,
    heading: 'Your payment is still processing',
    body: () =>
      "We'll email you as soon as it completes. There's nothing else you need to do — you can safely close this window.",
  },
};

/** Lifted from the design's "Why this can happen" card. */
const FAILURE_REASONS = [
  'Your bank declined the payment or needs to approve it',
  "There weren't enough available funds at the time",
  'The authorisation timed out before it completed',
];

/**
 * How a payment ended, when it did not succeed (GIFTWEB-26 AC 5, AC 6, AC 8).
 *
 * The layout is the design's "Payment failed" board; the headlines are the
 * ACs', which are status-specific where the board has only one. Nothing is
 * reset here — the confirmation screen still needs the amount, the occasion and
 * the child's name, and a contributor who retries needs their contribution.
 */
function PaymentOutcome() {
  const isHydrated = useHydrated();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state } = useLocation();

  const code = useGiftInviteCode();
  const contributionId = useContributionId();
  const amount = useAmount();
  const childFirstName = useChildFirstName();
  const photoSignedUrl = useGiftImageUrl();
  const showChildPhoto = useShowChildPhoto();
  const paymentId = useOpenBankingId();
  const clearPaymentAttempt = useClearPaymentAttempt();

  const { mutateAsync: restartOpenBanking } = useOpenBankingRestartMutation();

  const [isRestarting, setIsRestarting] = useState(false);
  const [restartFailed, setRestartFailed] = useState(false);

  // Query string first so a refresh keeps the outcome; router state is what the
  // callback used to pass and is still honoured.
  const outcome = readOutcome(
    searchParams.get('status') ?? (state as { status?: string } | null)?.status,
  );

  const amountLabel = formatCurrency({ amount, precision: 0 });
  const child = childFirstName?.trim();
  const giftPhrase = child
    ? `your ${amountLabel} gift to ${child}`
    : `your ${amountLabel} gift`;
  const potPhrase = child ? `${child}'s pot` : 'their pot';

  const copy = COPY[outcome];
  const isPending = outcome === 'pending';

  const tryAgain = async () => {
    setIsRestarting(true);
    setRestartFailed(false);

    try {
      const response = await restartOpenBanking({
        uuid: contributionId,
        code,
      });
      const authoriseUrl: string | undefined =
        response?.data?.truelayer?.authoriseUrl;

      if (!authoriseUrl) {
        throw new Error('No authorise URL in the open banking restart response');
      }

      redirectToExternal(authoriseUrl);
    } catch (error) {
      miaConsoleLog('error', {
        event: 'payment_outcome_restart_failed',
        contributionId,
        giftInviteCode: code,
        outcome,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      setRestartFailed(true);
      setIsRestarting(false);
    }
  };

  if (!isHydrated) return null;

  const summary = (
    <GiftSummaryChip
      firstName={childFirstName}
      photoUrl={photoSignedUrl}
      showPhoto={showChildPhoto}
      amountLabel={amountLabel}
    />
  );

  return (
    <PaymentScreen
      className='payment-outcome-page'
      summary={summary}
      icon={<OutcomeMark tone={isPending ? 'pending' : 'warning'} />}
      banner={
        <StatusBanner tone={isPending ? 'pending' : 'warning'}>
          {copy.banner(giftPhrase)}
        </StatusBanner>
      }
      heading={copy.heading}
      body={copy.body(potPhrase)}
      action={
        isPending ? (
          <Button
            className='block w-full'
            onClick={() =>
              navigate(
                `/easybanking/success?payment_id=${encodeURIComponent(paymentId ?? '')}&status=${openBankingStatuses.pending}`,
                { replace: true },
              )
            }
          >
            Continue
          </Button>
        ) : (
          <>
            {restartFailed && (
              <p role='alert' className='text-[13px] text-[#9f1239]'>
                We couldn't reach your bank just then. Please try once more, or
                use Need help.
              </p>
            )}
            <Button
              className='block w-full'
              onClick={tryAgain}
              disabled={isRestarting || !contributionId || !code}
            >
              {isRestarting ? 'Taking you to your bank…' : 'Try again'}
            </Button>
            {code && (
              <Button variant='outline' asChild className='flex w-full'>
                {/* Forget the attempt on the way out, or the landing page
                    resumes it straight back here (AC 10). */}
                <Link to={`/?code=${code}`} onClick={clearPaymentAttempt}>
                  Back to the start
                </Link>
              </Button>
            )}
          </>
        )
      }
    >
      {/* Only for a failure: someone who cancelled knows perfectly well why,
          and "your bank declined it" would be plainly wrong. */}
      {outcome === 'failed' && <ReasonsCard />}
    </PaymentScreen>
  );
}

type Tone = 'warning' | 'pending';

const TONES: Record<Tone, { strip: string; text: string; mark: string; dot: string }> = {
  // Amber, per the design's failed board.
  warning: {
    strip: 'border-[#f0e2b8] bg-[#fdf6e6]',
    text: 'text-[#5c3d05]',
    mark: 'bg-[#fdf1da] text-[#b0710a]',
    dot: 'bg-[#b0710a]',
  },
  // Blue: still in progress is not a failure, and amber would read as one.
  pending: {
    strip: 'border-[#d8e9f3] bg-[#f7fbfd]',
    text: 'text-[#1f4d66]',
    mark: 'bg-[#d8e9f3] text-[#2c6d94]',
    dot: 'bg-[#7fb6d6]',
  },
};

function StatusBanner({
  tone,
  children,
}: {
  tone: Tone;
  children: React.ReactNode;
}) {
  const t = TONES[tone];
  return (
    <div
      role='status'
      className={`flex w-full items-center gap-[11px] rounded-2xl border px-[15px] py-[13px] text-left ${t.strip}`}
    >
      <span
        aria-hidden='true'
        className={`flex size-[26px] shrink-0 items-center justify-center rounded-full ${
          tone === 'warning' ? 'bg-[#b0710a]' : 'bg-[#7fb6d6]'
        }`}
      >
        {tone === 'warning' ? (
          <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2.6' strokeLinecap='round'>
            <path d='M18 6 6 18M6 6l12 12' />
          </svg>
        ) : (
          <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2.6' strokeLinecap='round' strokeLinejoin='round'>
            <circle cx='12' cy='12' r='9' />
            <path d='M12 7v5l3 2' />
          </svg>
        )}
      </span>
      <span className={`text-[13.5px] leading-[1.4] font-semibold ${t.text}`}>
        {children}
      </span>
    </div>
  );
}

function OutcomeMark({ tone }: { tone: Tone }) {
  const t = TONES[tone];
  return (
    <span
      aria-hidden='true'
      className={`flex size-16 items-center justify-center rounded-full ${t.mark}`}
    >
      {tone === 'warning' ? (
        <svg width='30' height='30' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <path d='M10.3 3.8 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0z' />
          <path d='M12 9v4M12 17h.01' />
        </svg>
      ) : (
        <svg width='30' height='30' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <circle cx='12' cy='12' r='9' />
          <path d='M12 7v5l3 2' />
        </svg>
      )}
    </span>
  );
}

function ReasonsCard() {
  return (
    <div className='w-full rounded-[15px] border border-[#eceae3] bg-white px-4 py-[15px] text-left'>
      <h2 className='text-[12px] font-bold tracking-[0.04em] text-[#8a938c] uppercase'>
        Why this can happen
      </h2>
      <ul className='mt-[11px] flex flex-col gap-[9px]'>
        {FAILURE_REASONS.map((reason) => (
          <li key={reason} className='flex items-start gap-[9px]'>
            <span
              aria-hidden='true'
              className={`mt-[7px] size-[5px] shrink-0 rounded-full ${TONES.warning.dot}`}
            />
            <span className='text-[13px] leading-[1.45] text-[#3a423d]'>
              {reason}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default PaymentOutcome;
